import { useEffect, useState } from 'react';
import { getAllData } from 'apis/customApiHandler';
import { useSelector } from 'react-redux';
import { Input, Button,Select } from 'styled';
import { TH } from 'styled/table';
import useDebounce from 'hooks/useDebounce';
import { useQuery } from 'react-query';
import View from './view';
import Spinner from 'components/Spinner';
import { IoMdArrowBack, IoMdArrowForward } from 'react-icons/io';

const PAGE_SIZE = 150;

function MaterialMaster() {
  // searchInput is what the user is typing; search is what the query runs on. It
  // still follows the input after the debounce, but the Search button (and Enter)
  // applies it straight away instead of making the user wait.
  const [searchInput, setSearchInput] = useState('');
  const debounced = useDebounce(searchInput, 500);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("all");

  useEffect(() => {
    setSearch(debounced);
  }, [debounced]);

  const applySearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const currentUser = useSelector((s) => s.user.currentUser);

  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
  } = useQuery(
    ['material-master', { search: search ?? '', page, status }],
    () =>
      getAllData('/material-master', {
        searchQuery: search ?? '',
        limit: PAGE_SIZE,
        page,
        status
      }),
    {
      keepPreviousData: true,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    }
  );

  const records = data?.data ?? [];
  const totalData = data?.paginate?.totalData ?? 0;
  const totalPage = data?.paginate?.totalPage ?? 1;

  return (
    <div>
      <div className="m-10 flex gap-4 items-center flex-wrap">
        {/* the field grows to fill the row instead of claiming a whole line of
            its own, which used to push everything beside it onto a second row */}
        <div className="flex-1 min-w-[16rem]">
          <Input
            type="search"
            value={searchInput}
            placeholder="Search By Material No, Material Description"
            onChange={(e) => {
              setSearchInput(e.target.value);
              setPage(1);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') applySearch();
            }}
          />
        </div>

        <Button type="button" bgPrimary className="!px-8" onClick={applySearch}>
          Search
        </Button>

        <Select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
          className="w-40 max-w-xs"
        >
          <option value="all">All</option>
          <option value="enabled">Enabled</option>
          <option value="disabled">Disabled</option>
        </Select>
      </div>

      <div className="flex flex-col m-2 lg:m-10">
        <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
            <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <TH scope="col">S.N.</TH>
                    <TH scope="col">TYRE SIZE</TH>
                    <TH scope="col">Material No</TH>
                    <TH scope="col">MATERIAL DESCRIPTION</TH>
                    <TH scope="col">ORIGINAL NSD</TH>
                    <TH scope="col">UPDATED BY</TH>
                    <TH scope="col">CREATED AT</TH>
                    <TH scope="col">ACTIONS</TH>
                  </tr>
                </thead>

                <tbody className="bg-white divide-y divide-gray-200">
                  {isLoading && (
                    <tr>
                      <td colSpan={8} className="py-8">
                        <div className="flex justify-center">
                          <Spinner />
                        </div>
                      </td>
                    </tr>
                  )}

                  {isError && !isLoading && (
                    <tr>
                      <td colSpan={8} className="p-6 text-sm text-center text-red-600">
                        Failed: {String(error?.message || 'Unknown error')}
                      </td>
                    </tr>
                  )}

                  {!isLoading && !isError && records.length === 0 && (
                    <tr>
                      <td colSpan={8} className="text-center p-6 text-sm text-gray-500">
                        No data
                      </td>
                    </tr>
                  )}

                  {!isLoading &&
                    !isError &&
                    records.map((m, idx) => (
                      <View
                        key={m._id}
                        material={m}
                        index={(page - 1) * PAGE_SIZE + idx} 
                      />
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {totalPage > 0 && (
          <div className="container mx-auto flex justify-center items-center gap-2 py-5">
            <Button
              onClick={() => setPage((p) => Math.max(p - 1, 1))}
              disabled={page === 1}
              className="disabled:pointer-events-none disabled:opacity-70 px-0"
              primary
            >
              <div className="flex gap-1 items-center">
                <IoMdArrowBack />
                Prev Page
              </div>
            </Button>

            <span className="text-gray-600 px-4">
              {`showing: ${
                totalData === 0 ? 0 : (page - 1) * PAGE_SIZE + 1
              } - ${Math.min(totalData, page * PAGE_SIZE)} of ${totalData}`}
              {isFetching && (
                <span className="ml-2 text-xs text-gray-400">(updating…)</span>
              )}
            </span>

            <Button
              onClick={() => setPage((p) => Math.min(p + 1, totalPage))}
              disabled={page >= totalPage}
              className="disabled:pointer-events-none disabled:opacity-70 px-0"
              primary
            >
              <div className="flex gap-1 items-center">
                Next Page
                <IoMdArrowForward />
              </div>
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export default MaterialMaster;
